import { Button, Flex, Image, Input, message, Space, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { REPORT_IDS_KEY, REPORT_INDEX_KEY } from '.';
import { ReportAuditResult, ReportDetail, ReportStatus } from '@/types/report';
import { useQuery } from '@tanstack/react-query';
import { auditReport, getReportCategory, getReportDetail } from '@/api/report';
import './index.css';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useAtomValue } from 'jotai';

type ReportIssueObject = Record<
  string,
  { label: string; children: { [key: string]: string } }
>;

interface JudgeItemProps {
  reportDetail?: ReportDetail;
  reportIssueObject: ReportIssueObject;
  refresh: () => void;
  type?: number; // 1 话题 0 作品（列表带入，audit 时传递）
}

const JudgeItem = ({
  reportDetail,
  reportIssueObject,
  refresh,
  type: reportType,
}: JudgeItemProps) => {
  const isMobile = useAtomValue(isMobileAtom);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    width: 300,
    flexShrink: 0,
  };
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const [remark, setRemark] = useState('');

  const renderLatestJudgement = (latestJudgement?: ReportStatus) => {
    switch (latestJudgement) {
      case ReportStatus.NOT_YET:
        return <Tag>Not Yet</Tag>;
      case ReportStatus.BANNED:
        return <Tag color="red">BAN</Tag>;
      case ReportStatus.OMMITTED:
        return <Tag color="green">OMIT</Tag>;
      default:
        return '-';
    }
  };

  const handleAudit = async (result: ReportAuditResult) => {
    if (!reportDetail) {
      return;
    }
    const resp = await auditReport({
      auditResult: result,
      reportId: reportDetail.reportId,
      conclusion: remark,
      ...(reportType !== undefined && reportType !== null && { type: reportType }),
    });
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success('Audit success');
    setRemark('');
    refresh();
  };

  const handleCancel = () => {
    navigate('/report');
  };

  return (
    <div>
      {contextHolder}
      <div>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Name</div>
          <div>
            {reportDetail?.reportUserNickName || reportDetail?.reportUserName}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Supp ID</div>
          <div>{reportDetail?.userNumber}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>violating author SUPPID</div>
          <div>{reportDetail?.theReportUserId || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork ID</div>
          <div>{reportDetail?.artworkId || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Latest Judgement</div>
          <div>{renderLatestJudgement(reportDetail?.status)}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork Title</div>
          <div>{reportDetail?.artworkTitle || '-'}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Artwork Decription</div>
          <div style={{ maxWidth: 500, wordWrap: 'break-word' }}>
            {reportDetail?.artworkDescription || '-'}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Report Reason</div>
          <div>
            {reportIssueObject[reportDetail?.categoryId || '']?.label || '-'}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Report Issue</div>
          <div>
            {reportIssueObject[reportDetail?.categoryId || '']?.children[
              reportDetail?.subCategoryId || ''
            ] || '-'}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Report Description</div>
          <div>{reportDetail?.userRemark || '-'}</div>
        </Flex>
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Artwork Image</div>
          <Flex style={{ overflowX: 'auto', marginTop: 10 }}>
            <Image.PreviewGroup>
              {reportDetail?.artworkImageUrls?.map((image) => (
                <Image src={image} width={isMobile ? 100 : 150} key={image} />
              ))}
            </Image.PreviewGroup>
          </Flex>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Report Pictures</div>
          <Flex style={{ overflowX: 'auto', marginTop: 10 }}>
            <Image.PreviewGroup>
              {reportDetail?.imageUrls?.map((image) => (
                <Image src={image} width={isMobile ? 100 : 150} key={image} />
              ))}
            </Image.PreviewGroup>
          </Flex>
        </div>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>Review Remark</div>
          <Input.TextArea
            rows={4}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Flex>
      </div>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space>
          <Button type="primary" onClick={() => handleAudit('reject')}>
            OMIT
          </Button>
          <Button type="primary" danger onClick={() => handleAudit('approve')}>
            BAN
          </Button>
          <Button onClick={handleCancel}>CANCEL</Button>
        </Space>
      </div>
    </div>
  );
};

const JudgePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentIndex, setCurrentIndex] = useState(
    parseInt(searchParams.get(REPORT_INDEX_KEY) || '0')
  );

  const reportIdsRef = useRef<string[]>([]);
  const [reportId, setReportId] = useState<number>();

  const { data: reportCategory } = useQuery({
    queryKey: ['reportCategory'],
    queryFn: getReportCategory,
    refetchOnWindowFocus: false,
  });

  const reportIssueObject = useMemo<ReportIssueObject>(() => {
    if (!reportCategory) return {};
    const _reportIssueObject: ReportIssueObject = {};
    reportCategory.data.forEach((item) => {
      _reportIssueObject[item.categoryId.toString()] = {
        label: item.categoryName,
        children: {},
      };
      item.children.forEach((child) => {
        _reportIssueObject[item.categoryId.toString()].children[
          child.categoryId.toString()
        ] = child.categoryName;
      });
    });
    return _reportIssueObject;
  }, [reportCategory]);

  const { data: reportDetail, refetch: refresh } = useQuery({
    queryKey: ['reportDetail', reportId],
    queryFn: async () => {
      if (!reportId) {
        return;
      }
      const resp = await getReportDetail(reportId);
      return resp.data;
    },
    enabled: !!reportId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const ids = searchParams.get(REPORT_IDS_KEY)?.split(',') || [];
    if (!reportIdsRef.current.length && ids.length) {
      reportIdsRef.current = ids;
      setReportId(parseInt(ids[currentIndex]));
    }
  }, [currentIndex, searchParams]);

  const handlePrevClick = useCallback(() => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const prevId = reportIdsRef.current[newIndex];
    if (prevId) {
      const params = new URLSearchParams(searchParams);
      params.set(REPORT_INDEX_KEY, newIndex.toString());
      setSearchParams(params);
      setReportId(parseInt(prevId));
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNextClick = useCallback(() => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    const nextId = reportIdsRef.current[newIndex];
    if (nextId) {
      const params = new URLSearchParams(searchParams);
      params.set(REPORT_INDEX_KEY, newIndex.toString());
      setSearchParams(params);
      setReportId(parseInt(nextId));
    }
  }, [currentIndex, searchParams, setSearchParams]);

  return (
    <div className="report-view-container">
      <div className="pagination-btn-container">
        <Button
          type="primary"
          className="pagination-btn-left"
          disabled={currentIndex === 0}
          onClick={handlePrevClick}
          shape="circle"
        >
          &lt;
        </Button>
      </div>

      <div className="report-view-item-container">
        <JudgeItem
          reportDetail={reportDetail}
          type={searchParams.get('type') != null ? Number(searchParams.get('type')) : undefined}
          reportIssueObject={reportIssueObject}
          refresh={refresh}
        />
      </div>

      <div className="pagination-btn-container">
        <Button
          type="primary"
          className="pagination-btn-right"
          disabled={currentIndex === reportIdsRef.current.length - 1}
          onClick={handleNextClick}
          shape="circle"
        >
          &gt;
        </Button>
      </div>
    </div>
  );
};

export default JudgePage;
